import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, MapPin, Users, Trophy, GraduationCap, BookOpen, Target } from "lucide-react";
import { INSTITUTE, STATS, BRANCHES } from "@/lib/content";
import useSEO from "@/hooks/useSEO";

export default function About() {
  useSEO({
    title: "About Kothari Classes | JEE, NEET & Foundation Coaching in Jalgaon",
    description: "Kothari Classes is Jalgaon's most trusted coaching institute for JEE, NEET, MH-CET and Foundation courses (8th–12th). 35+ years of excellence, personal attention, strong results.",
    canonical: "https://www.kothariclasses.in/about",
  });

  return (
    <div data-testid="about-page">
      {/* Header */}
      <section className="bg-white py-16 lg:py-24 border-b border-gray-200 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] bg-red-600/5 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="eyebrow">About Kothari Classes · Jalgaon</span>
              <h1 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1] text-gray-950">
                Building Jalgaon's<br/>future toppers since 1989.
              </h1>
              <p className="mt-5 text-gray-600 text-lg max-w-xl">
                For 35+ years, Kothari Classes has been Jalgaon's most trusted coaching institute for <strong>JEE, NEET, MH-CET and Foundation courses (8th–12th)</strong>. Personal attention, expert faculty and a relentless focus on results.
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/admissions" className="btn-primary rounded-none text-sm" data-testid="about-enroll">
                  Book Free Demo <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/courses" className="btn-secondary rounded-none text-sm" data-testid="about-courses">Explore Courses</Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-full h-full border-2 border-red-600" />
                <img
                  src="https://images.pexels.com/photos/8617769/pexels-photo-8617769.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Kothari Classes Jalgaon classroom"
                  className="relative w-full h-[380px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {STATS.map((s, i) => (
            <div key={s.label} className="py-4 border-l-2 border-red-600 pl-5" data-testid={`about-stat-${i}`}>
              <div className="font-display text-4xl lg:text-5xl font-black text-gray-950 tracking-tighter">{s.value}</div>
              <div className="text-xs lg:text-sm text-gray-500 mt-2 uppercase tracking-widest">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Story - 600 words */}
      <section className="bg-white py-16 lg:py-24" data-testid="about-story">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="max-w-3xl">
            <span className="eyebrow">Our Story</span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-gray-950">
              The most trusted coaching institute in Jalgaon.
            </h2>
          </div>

          <div className="prose-custom space-y-6 text-gray-700 text-base lg:text-[17px] leading-relaxed max-w-3xl">
            <p>
              <strong>Kothari Classes</strong> was founded in the heart of Jalgaon over three decades ago with a single belief — that every student deserves <strong>personal attention</strong> and a teacher who actually cares about their result. From our first classroom at <strong>Baliram Peth</strong> to our current two branches (Baliram Peth and Yogeshwar Nagar), that belief has not changed. What has grown is the number of families who have trusted us with their child's most important academic years.
            </p>
            <p>
              Today, Kothari Classes is recognized as one of the <strong>top JEE coaching classes in Jalgaon</strong>, a leading name for <strong>NEET classes in Jalgaon</strong>, and the go-to institute for <strong>MH-CET preparation</strong>. Our coaching spans every stage — from <strong>10th CBSE coaching classes in Jalgaon</strong> to full <strong>11th and 12th Science</strong> integrated batches with IIT-JEE, NEET and MHT-CET tracks. We also run structured <strong>Foundation Courses for 8th, 9th and 10th</strong> students aligned directly with IIT-JEE and NEET pathways.
            </p>

            <h3 className="font-display text-2xl font-black text-gray-950 mt-10 mb-2">Our teaching methodology</h3>
            <p>
              Our philosophy is simple: concepts first, competition next. Every chapter is taught from NCERT-first fundamentals before we layer in the advanced problem-solving needed for JEE, NEET and MHT-CET. Batch sizes are intentionally kept small so that no doubt goes unanswered and no student fades into the background. Weekly chapter tests, benchmark mock tests and detailed performance analysis sessions are built into every program — not optional add-ons. Parents receive regular PTA reports so they always know where their child stands.
            </p>

            <h3 className="font-display text-2xl font-black text-gray-950 mt-10 mb-2">Why parents in Jalgaon trust Kothari Classes</h3>
            <p>
              The families who come to us rarely leave. Many of our current students are younger siblings of students we coached five or ten years ago. That trust is built on three commitments we refuse to compromise — <strong>personal attention</strong> from every faculty member, <strong>foundation excellence</strong> that stays with the student for life, and a <strong>result-driven classroom</strong> where hard work is tracked, celebrated and rewarded. Our students have gone on to MBBS, BDS, NITs, top engineering colleges across Maharashtra and leading state medical colleges.
            </p>

            <h3 className="font-display text-2xl font-black text-gray-950 mt-10 mb-2">Foundation excellence, from 8th to 12th</h3>
            <p>
              The Foundation Course at Kothari Classes is specifically designed for students who want an early edge in JEE and NEET. We introduce competitive reasoning, advanced problem-solving and NTSE/Olympiad training without sacrificing school syllabus. By the time our Foundation students enter 11th, they are already two steps ahead of their peers — confident with PCM and PCB at a level most students reach only in 12th.
            </p>

            <h3 className="font-display text-2xl font-black text-gray-950 mt-10 mb-2">Our approach to student success</h3>
            <p>
              We don't treat students as registration numbers. Every student at Kothari Classes gets a <strong>personal mentor</strong>, a tailored study plan, and access to our faculty beyond classroom hours. We know that the real difference between a 70% and a 95% score is rarely talent — it is structured guidance, consistent effort and a coaching environment that holds students to a higher standard. That is exactly what our <strong>Baliram Peth</strong> and <strong>Yogeshwar Nagar</strong> branches have delivered for 35+ years.
            </p>
            <p>
              If you are a parent or student in Jalgaon evaluating coaching classes, we invite you to <Link to="/admissions" className="text-red-600 font-semibold hover:underline">book a free demo</Link> and experience our classrooms first-hand. Meet the faculty, see how we teach, and decide for yourself why Kothari Classes continues to be Jalgaon's most trusted name in JEE, NEET, MH-CET and foundation coaching.
            </p>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-gray-50 py-16 lg:py-20" data-testid="about-pillars">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <span className="eyebrow">What we stand for</span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-black tracking-tight text-gray-950">Four pillars. Zero compromise.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200">
            {[
              { icon: Users, title: "Personal Attention", desc: "Small capped batches, 1-on-1 mentor support for every student." },
              { icon: GraduationCap, title: "Foundation Excellence", desc: "8th–10th Foundation aligned with IIT-JEE and NEET from day one." },
              { icon: Target, title: "NEET · JEE · MH-CET", desc: "Dedicated senior-section tracks with weekly mock-test series." },
              { icon: Trophy, title: "Result-Driven", desc: "Structured methodology proven across CBSE, ICSE and State boards." },
            ].map((p, i) => (
              <div key={p.title} className="bg-white p-6 lg:p-8 card-lift" data-testid={`pillar-${i}`}>
                <div className="w-11 h-11 border border-red-200 bg-red-50 flex items-center justify-center text-red-600">
                  <p.icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-lg font-bold text-gray-950 mt-5">{p.title}</h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Branches quick */}
      <section className="bg-white py-14" data-testid="about-branches">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-5">
          {BRANCHES.map((b) => (
            <div key={b.id} className="border border-gray-200 p-6 card-lift">
              <span className="eyebrow">{b.short}</span>
              <h3 className="font-display text-xl font-bold text-gray-950 mt-2">{b.name}</h3>
              <div className="text-sm text-gray-600 mt-2 flex gap-2"><MapPin className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />{b.address}</div>
              <div className="flex gap-2 mt-5">
                <Link to="/contact" className="btn-primary rounded-none text-xs" data-testid={`about-contact-${b.id}`}>Visit Branch <ArrowRight className="w-3.5 h-3.5"/></Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-red-600 text-white py-14" data-testid="about-cta">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center justify-between gap-5">
          <div>
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-white/80">Admissions Open · 2026–27</div>
            <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tight mt-2">Start your success story at Kothari Classes.</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/admissions" className="bg-white text-red-600 font-semibold px-6 py-3.5 hover:bg-gray-100 transition-colors inline-flex items-center gap-2">Book Free Demo <ArrowRight className="w-4 h-4"/></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
